import { Box, type BoxProps } from '@mantine/core'
import { type DateInputProps } from '@mantine/dates'
import { clsx } from 'clsx'
import { format } from 'date-fns'
import { useEffect, useRef, useState } from 'react'

import { useTranslation } from '@flecto/translations'

import {
  DatePickerInput,
  type DatePickerInputProps,
} from '../DatePickerInput/DatePickerInput'
import {
  TimePickerInput,
  type TimePickerInputProps,
} from '../TimePickerInput/TimePickerInput'
import styles from './styles.module.css'

export type DateTimePickerInputProps = {
  value?: string | null
  fullWidth?: boolean
  defaultDateValue?: string | null
  defaultTimeValue?: string | null
  showCalendarIcon?: boolean
  showTimeIcon?: boolean
  isLoading?: boolean
  autoFocusDate?: boolean
  autoFocusTime?: boolean
  placeholderDate?: string
  placeholderTime?: string
  onChange?: (date: string) => void
} & Omit<DateInputProps, 'value' | 'onChange'> &
  Pick<
    TimePickerInputProps,
    'minTime' | 'maxTime' | 'intervalMinutes' | 'comboboxProps'
  >

export function DateTimePickerInput({
  size,
  value,
  fullWidth,
  defaultTimeValue = '00:00',
  isLoading,
  showCalendarIcon = true,
  showTimeIcon = true,
  autoFocusDate,
  autoFocusTime,
  onChange,
  ...props
}: DateTimePickerInputProps) {
  const { t } = useTranslation('shared', { keyPrefix: 'shared' })
  const [date, setDate] = useState<string | null>(null)
  const [time, setTime] = useState<string | null>(null)
  const inputTimeRef = useRef<HTMLInputElement>(null)

  const formatDate = (date: Date) =>
    `${format(date, 'yyyy-MM-dd')}T${format(date, 'HH:mm:ss')}`

  const placeholderDate =
    props.placeholderDate ?? t('datetimepickerinput.placeholder_date')
  const placeholderTime =
    props.placeholderTime ?? t('datetimepickerinput.placeholder_time')

  useEffect(() => {
    if (value) {
      const valueDate = Date.parse(value)

      if (valueDate) {
        const parseDate = new Date(valueDate)
        const valueHours = String(parseDate.getHours()).padStart(2, '0')
        const valueMinutes = String(parseDate.getMinutes()).padEnd(2, '0')
        const timeString = `${valueHours}:${valueMinutes}`

        setDate(format(parseDate, 'yyyy-MM-dd'))
        setTime(timeString)
      }
    } else {
      setDate(null)
      setTime(null)
    }
  }, [value])

  const propagateValue = (date: string | null, time: string | null) => {
    if (!date) {
      onChange?.('')
      return
    }

    const timeValue = time ?? '00:00'
    const [hours, minutes] = timeValue.split(':')

    const parsedDate = new Date(`${date}T00:00:00`)
    parsedDate.setHours(Number(hours))
    parsedDate.setMinutes(Number(minutes))

    setDate(format(parsedDate, 'yyyy-MM-dd'))
    setTime(timeValue)

    onChange?.(formatDate(parsedDate))
  }

  const handleChangeDate = (date: string) => {
    setDate(date)

    if (!date) {
      setTime(null)
      propagateValue(date, null)
      return
    }

    if (!time) {
      const newTime = defaultTimeValue ? defaultTimeValue : '00:00'
      setTime(newTime)
      propagateValue(date, newTime)
      return
    }

    propagateValue(date, time)
  }

  const handleChangeTime = (time: string | null) => {
    if (!time) {
      return
    }

    setTime(time)
    propagateValue(date, time)
  }

  const handleSelectDate = () => {
    if (!defaultTimeValue) {
      inputTimeRef.current?.focus()
    }
  }

  return (
    <Box
      {...(props as BoxProps)}
      className={clsx(styles.wrapper, props.className, styles[size ?? 'md'], {
        [styles.fullWidth]: fullWidth,
      })}
    >
      <DatePickerInput
        {...(props as DatePickerInputProps)}
        placeholder={placeholderDate}
        value={date}
        isLoading={isLoading}
        showCalendarIcon={showCalendarIcon}
        className={styles.inputDate}
        clearable={props.clearable}
        autoFocus={autoFocusDate}
        onChange={handleChangeDate}
        onSelectDate={handleSelectDate}
        data-testid='date-time-picker-input-date'
      />
      <TimePickerInput
        {...(props as TimePickerInputProps)}
        ref={inputTimeRef}
        value={time}
        placeholder={placeholderTime}
        showTimeIcon={showTimeIcon}
        className={styles.inputTime}
        clearable={props.clearable}
        autoFocus={autoFocusTime}
        disabled={isLoading || props.disabled || Boolean(!date)}
        onChange={handleChangeTime}
        data-testid='date-time-picker-input-time'
      />
    </Box>
  )
}
